import React, { useState } from 'react';
import { Shield, ChevronDown, Lock, Eye, Database, UserCheck, AlertTriangle, HelpCircle } from 'lucide-react';

interface Section {
  id: string;
  title: string;
  icon: JSX.Element;
  content: string | string[];
}

function PrivacyPolicy() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const sections: Section[] = [
    {
      id: 'intro',
      title: 'Общие положения',
      icon: <Shield className="w-6 h-6" />,
      content: [
        'Настоящая Политика обработки персональных данных (далее – Политика) разработана в соответствии с требованиями Федерального закона от 27.07.2006 №152-ФЗ «О персональных данных».',
        'Политика действует в отношении всей информации, которую RDPcargo может получить о Пользователе во время использования им интернет-сайта rdpcargo.netlify.app, любого из сервисов или услуг (продуктов) Компании (далее – Сервисы) и в ходе исполнения любых договоров (соглашений), заключенных с Пользователем.'
      ]
    },
    {
      id: 'terms',
      title: 'Термины и определения',
      icon: <HelpCircle className="w-6 h-6" />,
      content: [
        '"Персональные данные" - любая информация, относящаяся к прямо или косвенно определенному или определяемому физическому лицу (субъекту персональных данных).',
        '"Обработка персональных данных" - любое действие (операция) или совокупность действий (операций), совершаемых с использованием средств автоматизации или без использования таких средств с персональными данными.',
        '"Пользователь" – лицо, имеющее доступ к Сайту, посредством сети Интернет и использующее Сервисы Компании.',
        '"Сайт" – интернет-сайт Компании по адресу www.rdpcargo.netlify.app/'
      ]
    },
    {
      id: 'data',
      title: 'Собираемые данные',
      icon: <Database className="w-6 h-6" />,
      content: [
        'Персональная информация, которую Пользователь предоставляет о себе самостоятельно при регистрации (создании учётной записи) или в процессе использования Сервисов.',
        'Данные, которые автоматически передаются Сайту в процессе его использования с помощью установленного на устройстве Пользователя программного обеспечения, в том числе IP-адрес, информация из cookie, информация о браузере пользователя.'
      ]
    },
    {
      id: 'processing',
      title: 'Обработка данных',
      icon: <Lock className="w-6 h-6" />,
      content: [
        'Обработка персональных данных Пользователя осуществляется без ограничения срока, любым законным способом, в том числе в информационных системах персональных данных с использованием средств автоматизации или без использования таких средств.',
        'Персональные данные Пользователя могут быть переданы уполномоченным органам государственной власти Российской Федерации только по основаниям и в порядке, установленным законодательством Российской Федерации.'
      ]
    },
    {
      id: 'purpose',
      title: 'Цели сбора данных',
      icon: <Eye className="w-6 h-6" />,
      content: [
        'Идентификация Пользователя в рамках Сервисов, предоставляемых Компанией.',
        'Предоставление Пользователю доступа к персонализированным ресурсам Сайта.',
        'Установление с Пользователем обратной связи, включая направление уведомлений, запросов.',
        'Подтверждение достоверности и полноты персональных данных, предоставленных Пользователем.',
        'Создание учетной записи Пользователя.'
      ]
    },
    {
      id: 'consent',
      title: 'Согласие пользователя',
      icon: <UserCheck className="w-6 h-6" />,
      content: [
        'Используя Сайт, Пользователь выражает свое согласие с условиями настоящей Политики конфиденциальности.',
        'Пользователь подтверждает, что, давая такое согласие, он действует свободно, своей волей и в своем интересе.'
      ]
    },
    {
      id: 'warning',
      title: 'Важное примечание',
      icon: <AlertTriangle className="w-6 h-6" />,
      content: [
        'Компания не проверяет достоверность персональной информации, предоставляемой Пользователями.',
        'Компания исходит из того, что Пользователь предоставляет достоверную и достаточную персональную информацию и поддерживает эту информацию в актуальном состоянии.'
      ]
    }
  ];

  const toggleSection = (id: string) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fbfbfd] to-white pt-32 px-6">
      <div className="max-w-[780px] mx-auto">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="inline-block p-4 bg-[#0171E3]/10 rounded-full mb-6">
            <Shield className="w-12 h-12 text-[#0171E3]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-semibold text-[#1d1d1f] mb-6">
            Политика конфиденциальности
          </h1>
          <p className="text-xl text-[#86868b]">
            Мы ценим вашу конфиденциальность и защищаем ваши персональные данные
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section, index) => (
            <div
              key={section.id}
              className={`bg-white rounded-2xl shadow-sm overflow-hidden transition-all duration-300 animate-fade-in`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="mr-4 text-[#0171E3]">
                    {section.icon}
                  </div>
                  <span className="font-medium text-[#1d1d1f]">{section.title}</span>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-[#86868b] transition-transform duration-300 ${
                    expandedSection === section.id ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`px-6 overflow-hidden transition-all duration-300 ${
                  expandedSection === section.id ? 'max-h-[500px] py-4' : 'max-h-0'
                }`}
              >
                {Array.isArray(section.content) ? (
                  <ul className="space-y-4 text-[#86868b]">
                    {section.content.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[#86868b]">{section.content}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-12 text-center animate-fade-in" style={{ animationDelay: '800ms' }}>
          <p className="text-[#86868b] mb-4">
            Остались вопросы о нашей политике конфиденциальности?
          </p>
          <a
            href="/contacts"
            className="inline-flex items-center text-[#0171E3] hover:text-[#0156aa] transition-colors"
          >
            Свяжитесь с нами
            <ChevronDown className="w-5 h-5 ml-1 rotate-[-90deg]" />
          </a>
        </div>
      </div>

      <style>{`
        .animate-fade-in {
          opacity: 0;
          transform: translateY(20px);
          animation: fade-in 0.5s ease-out forwards;
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default PrivacyPolicy;